import { useState, useEffect } from 'react';
import { IHeaderParams } from 'ag-grid-community';
import { Checkbox } from 'common/form';
import { TableV3SelectionMode } from 'common/table/types/TableV3';

type TableHeaderSelectionCheckboxProps = {
  params: IHeaderParams;
  selectionMode?: TableV3SelectionMode;
  onDeselectAll?: () => void;
};

export const TableHeaderSelectionCheckbox = ({
  params,
  selectionMode,
  onDeselectAll
}: TableHeaderSelectionCheckboxProps): React.ReactElement | null => {
  const [selectionState, setSelectionState] = useState<'none' | 'some' | 'all'>(
    'none'
  );

  const calculateSelectionState = () => {
    if (!params.api) {
      return 'none';
    }

    const selectedNodes = params.api.getSelectedNodes();
    if (!selectedNodes || !Array.isArray(selectedNodes)) {
      return 'none';
    }

    let selectableCount = 0;
    params.api.forEachNodeAfterFilter((node) => {
      if (node.selectable !== false) selectableCount++;
    });

    if (selectedNodes.length === 0) {
      return 'none';
    } else if (selectedNodes.length === selectableCount) {
      return 'all';
    } else {
      return 'some';
    }
  };

  useEffect(() => {
    setSelectionState(calculateSelectionState());

    // Check if params.api exists before adding event listeners
    if (!params.api) {
      return;
    }

    const onSelectionChanged = () => {
      setSelectionState(calculateSelectionState());
    };

    const onModelUpdated = () => {
      setSelectionState(calculateSelectionState());
    };

    params.api.addEventListener('selectionChanged', onSelectionChanged);
    params.api.addEventListener('modelUpdated', onModelUpdated);

    return () => {
      if (params.api) {
        params.api.removeEventListener('selectionChanged', onSelectionChanged);
        params.api.removeEventListener('modelUpdated', onModelUpdated);
      }
    };
  }, []);

  const handleSelectionChange = (value: boolean) => {
    // Check if params.api exists before calling methods
    if (!params.api) {
      return;
    }

    if (value) {
      params.api.selectAll('filtered');
      return;
    }

    if (onDeselectAll) {
      onDeselectAll();
    } else {
      params.api.deselectAll();
    }
  };

  // Don't show header checkbox for single selection mode
  if (selectionMode === 'singleRow') {
    return null;
  }

  return (
    <Checkbox
      field={{}}
      value={selectionState === 'all'}
      onChange={handleSelectionChange}
    />
  );
};
